import { createSlice } from 'redux-starter-kit'
import { createSelector } from 'reselect'
import { call, put, all, takeLatest } from 'redux-saga/effects'

import { reportsApi } from '@edulastic/api'

import { RESET_ALL_REPORTS } from '../../reportsRedux'

const initialState = {
  loading: false,
  data: null,
  // reportType the current `data` was fetched for (pairs with data.contextId).
  dataReportType: null,
  error: null,
}

// -----|-----|-----|-----| SLICE BEGIN |-----|-----|-----|----- //

const slice = createSlice({
  slice: 'aiSummary',
  initialState: { ...initialState },
  reducers: {
    fetchAISummaryRequest: (state) => {
      state.loading = true
      state.error = null
      state.data = null
      state.dataReportType = null
    },
    // Success/error are ignored once the request was reset (filter change, unmount),
    // so a late response cannot repopulate the panel for different filters.
    fetchAISummaryRequestSuccess: (state, { payload }) => {
      if (!state.loading) return
      state.loading = false
      state.data = payload.data
      state.dataReportType = payload.reportType ?? null
      state.error = null
    },
    fetchAISummaryRequestError: (state, { payload }) => {
      if (!state.loading) return
      state.loading = false
      state.error = payload.error
      state.data = null
      state.dataReportType = null
    },
    resetAISummary: () => ({ ...initialState }),
  },
  extraReducers: {
    [RESET_ALL_REPORTS]: () => ({ ...initialState }),
  },
})

export const { actions, reducer } = slice

// -----|-----|-----|-----| SLICE ENDED |-----|-----|-----|----- //

// =====|=====|=====|=====| =============== |=====|=====|=====|===== //

// -----|-----|-----|-----| SELECTORS BEGIN |-----|-----|-----|----- //

const stateSelector = (state) => state.reportReducer.reportAISummaryReducer

const loading = createSelector(stateSelector, (state) => state.loading)
const error = createSelector(stateSelector, (state) => state.error)
const data = createSelector(stateSelector, (state) => state.data)
const summary = createSelector(data, (summaryData) => summaryData?.summary)
const dataReportType = createSelector(
  stateSelector,
  (state) => state.dataReportType
)
const reportSummaryContextId = createSelector(
  data,
  (summaryData) => summaryData?.contextId
)

export const selectors = {
  loading,
  error,
  data,
  summary,
  dataReportType,
  reportSummaryContextId,
}

// -----|-----|-----|-----| SELECTORS ENDED |-----|-----|-----|----- //

// =====|=====|=====|=====| =============== |=====|=====|=====|===== //

// -----|-----|-----|-----| SAGAS BEGIN |-----|-----|-----|----- //

function* fetchAISummaryRequestSaga({ payload }) {
  try {
    const response = yield call(reportsApi.getAISummary, payload)
    yield put(
      actions.fetchAISummaryRequestSuccess({
        data: response,
        reportType: payload?.reportType,
      })
    )
  } catch (e) {
    yield put(actions.fetchAISummaryRequestError({ error: e }))
  }
}

export function* watcherSaga() {
  yield all([
    takeLatest(actions.fetchAISummaryRequest, fetchAISummaryRequestSaga),
  ])
}

// -----|-----|-----|-----| SAGAS ENDED |-----|-----|-----|----- //
